import { Operator } from '@/sections/insights/queryBuilder/legacy/types/queryBuilder.types';
import { AssetStatus } from '@/types';
import { getRecentTimestamps } from '@/utils/date.util';

/**
 * Centralized asset status filter mapping
 * This is the single source of truth for asset status filtering logic
 * Used by both useAssets hook and QueryBuilder
 */
export interface AssetStatusFilter {
  field: string;
  operator: string | Operator;
  value: string | AssetStatusFilter[];
}

export const getAssetStatusFilterMap = (
  isActiveDirectory = false
): Record<string, AssetStatusFilter> => {
  const timestamps = getRecentTimestamps();

  return {
    // Active: For AD assets, just status='A'. For network assets, status='A' AND visited >= 5 days ago
    A: isActiveDirectory
      ? {
          field: 'status',
          operator: '=',
          value: 'A',
        }
      : {
          field: '',
          operator: 'AND',
          value: [
            { field: 'status', operator: '=', value: 'A' },
            {
              field: 'visited',
              operator: '>=',
              value: timestamps['5days'],
            },
          ],
        },

    // Inactive: status='A' AND visited < 5 days AND visited >= 20 days ago
    U: {
      field: '',
      operator: 'AND',
      value: [
        { field: 'status', operator: '=', value: 'A' },
        {
          field: 'visited',
          operator: '<',
          value: timestamps['5days'],
        },
        {
          field: 'visited',
          operator: '>=',
          value: timestamps['20days'],
        },
      ],
    },

    // Expiring: status='A' AND visited < 20 days ago
    I: {
      field: '',
      operator: 'AND',
      value: [
        { field: 'status', operator: '=', value: 'A' },
        {
          field: 'visited',
          operator: '<',
          value: timestamps['20days'],
        },
      ],
    },

    // Simple status filters
    [AssetStatus.Pending]: {
      field: 'status',
      operator: '=',
      value: AssetStatus.Pending,
    },
    [AssetStatus.Frozen]: {
      field: 'status',
      operator: 'STARTS WITH',
      value: AssetStatus.Frozen,
    },
    [AssetStatus.Deleted]: {
      field: 'status',
      operator: '=',
      value: AssetStatus.Deleted,
    },
  };
};

/**
 * Helper function to build asset status filters for queries
 * Handles both single and multiple status values
 */
export const buildAssetStatusFilter = (
  statusValues: string[],
  isActiveDirectory = false
): AssetStatusFilter | null => {
  if (!statusValues || statusValues.length === 0) {
    return null;
  }

  const filterMap = getAssetStatusFilterMap(isActiveDirectory);

  if (statusValues.length === 1) {
    // Single status value
    const statusFilter = filterMap[statusValues[0]];
    return statusFilter || null;
  }

  // Multiple status values - use OR logic
  const statusFilters = statusValues
    .map(status => filterMap[status])
    .filter(Boolean);

  if (statusFilters.length === 0) {
    return null;
  }

  return {
    field: '',
    operator: 'OR',
    value: statusFilters,
  };
};
